import type { RegisteredComponent } from "@builder.io/sdk-react";
import { isEditing, isPreviewing } from "@builder.io/sdk-react";

import { ContactUsForm } from "~/hec/components/organisms/contact-us-form/contact-us-form";
import type {
  ContactUsEditorState,
  ContactUsFormCopy,
} from "~/hec/components/organisms/contact-us-form/types";

import { contactUsFormInputs } from "./inputs";

/**
 * COM-735 — the Contact Us form as a drop-in block, so the Contact Us page (or
 * any page) can be built in Builder. Thin wrapper: maps Builder inputs onto the
 * `ContactUsForm` organism and spreads `attributes` on the root (`noWrap`
 * contract, CLAUDE.md §4). Page-body block only.
 *
 * Form only, by decision: the hours/phone callout and the Press / Vendor /
 * Partnerships / Careers contacts are ordinary authored content around it.
 *
 * `name: "ContactUsForm"` is the contract published content references; it
 * cannot be renamed once content uses it.
 */
interface BuilderContactUsFormProps extends ContactUsFormCopy {
  editorState?: ContactUsEditorState;
  attributes?: Record<string, unknown> & { className?: string };
}

const BuilderContactUsForm = ({
  submitLabel,
  successMessage,
  failureMessage,
  editorState = "ready",
  attributes,
}: BuilderContactUsFormProps) => {
  const { className, ...rest } = attributes ?? {};
  const inEditor = isEditing() || isPreviewing();
  return (
    <ContactUsForm
      copy={{ submitLabel, successMessage, failureMessage }}
      editorState={inEditor ? editorState : undefined}
      className={className}
      {...rest}
    />
  );
};

export const ContactUsFormDefinition = {
  component: BuilderContactUsForm,
  name: "ContactUsForm",
  friendlyName: "Contact Us form (FSA + HSA)",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/mail.svg",
  inputs: contactUsFormInputs,
} satisfies RegisteredComponent;

export default BuilderContactUsForm;
